// src/pages/PagesUtilisateurs/DashboardUsers/modules/ContactFormDisplay.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCheck,
  FaCheckDouble,
  FaEnvelope,
  FaEyeSlash,
  FaTrash,
} from "react-icons/fa";

import { toast } from "react-toastify";

import {
  deleteMessageContactForm,
  getAllMessagesContactForm,
  markMessageContactForm,
} from "../../../../services/contactformServices";

import { useContactForm } from "../../../../hooks/ContactFormContext";

import DashboardActionButton from "../ui/DashboardActionButton";
import DashboardEmptyState from "../ui/DashboardEmptyState";

import {
  formatDateTime,
  isReadFlag,
} from "../utils/dashboardUser.utils";

export default function ContactFormDisplay({
  userId,
  onCounts,
}) {
  const [messages, setMessages] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const {
    setUnreadCountContactForm,
  } = useContactForm();

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        setLoading(true);

        const data =
          await getAllMessagesContactForm(
            userId,
          );

        if (!mounted) return;

        setMessages(
          Array.isArray(data)
            ? data
            : [],
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Erreur lors du chargement des messages.",
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const { total, unread } =
    useMemo(() => ({
      total: messages.length,

      unread: messages.filter(
        (message) =>
          !isReadFlag(
            message.read_message,
          ),
      ).length,
    }), [messages]);

  useEffect(() => {
    setUnreadCountContactForm(
      unread,
    );

    onCounts?.({
      total,
      unread,
    });
  }, [
    total,
    unread,
    onCounts,
    setUnreadCountContactForm,
  ]);

  const toggleReadStatus = async (id) => {
    try {
      await markMessageContactForm(id);

      setMessages((previous) =>
        previous.map((message) =>
          message.id === id
            ? {
                ...message,

                read_message:
                  !isReadFlag(
                    message.read_message,
                  ),
              }
            : message,
        ),
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de changer l’état de lecture.",
      );
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Supprimer ce message de contact ?",
      )
    ) {
      return;
    }

    try {
      await deleteMessageContactForm(
        id,
      );

      setMessages((previous) =>
        previous.filter(
          (message) =>
            message.id !== id,
        ),
      );

      toast.success(
        "Message supprimé.",
      );
    } catch {
      toast.error(
        "Erreur lors de la suppression.",
      );
    }
  };

  const handleMarkAllAsRead =
    async () => {
      const unreadRows =
        messages.filter(
          (message) =>
            !isReadFlag(
              message.read_message,
            ),
        );

      if (!unreadRows.length) return;

      try {
        await Promise.all(
          unreadRows.map(
            (message) =>
              markMessageContactForm(
                message.id,
              ),
          ),
        );

        setMessages((previous) =>
          previous.map(
            (message) => ({
              ...message,
              read_message: true,
            }),
          ),
        );

        toast.success(
          "Tous les messages sont marqués comme lus.",
        );
      } catch {
        toast.error(
          "Impossible de tout marquer comme lu.",
        );
      }
    };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({
          length: 4,
        }).map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            {total} demande
            {total > 1 ? "s" : ""}
          </p>

          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {unread
              ? `${unread} non lue${
                  unread > 1 ? "s" : ""
                }`
              : "Tout est à jour"}
          </p>
        </div>

        <DashboardActionButton
          icon={FaCheckDouble}
          onClick={
            handleMarkAllAsRead
          }
          disabled={!unread}
        >
          Tout marquer lu
        </DashboardActionButton>
      </div>

      {!messages.length ? (
        <DashboardEmptyState
          icon={FaEnvelope}
          title="Aucune demande"
          description="Aucun nouveau message n'a été transmis depuis le formulaire du site."
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 md:block dark:border-slate-800">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-950/50">
                <tr className="text-left text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                  <th className="w-24 px-4 py-3">
                    Date
                  </th>

                  <th className="px-4 py-3">
                    Demande
                  </th>

                  <th className="w-28 px-4 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {messages.map(
                  (message) => {
                    const read =
                      isReadFlag(
                        message.read_message,
                      );

                    const {
                      date,
                      time,
                    } = formatDateTime(
                      message.created_at,
                    );

                    return (
                      <tr
                        key={message.id}
                        className={
                          read
                            ? ""
                            : "bg-blue-50/40 dark:bg-blue-500/5"
                        }
                      >
                        <td className="px-4 py-4 align-top">
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            {date}
                          </p>

                          <p className="text-[10px] text-slate-400">
                            {time}
                          </p>
                        </td>

                        <td className="px-4 py-4">
                          <p className="font-bold text-slate-950 dark:text-white">
                            {message.name}
                          </p>

                          <p className="mt-0.5 text-xs text-[#013E8E] dark:text-blue-300">
                            {message.email}
                          </p>

                          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                            {message.message}
                          </p>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <DashboardActionButton
                              compact
                              icon={
                                read
                                  ? FaEyeSlash
                                  : FaCheck
                              }
                              onClick={() =>
                                toggleReadStatus(
                                  message.id,
                                )
                              }
                            />

                            <DashboardActionButton
                              compact
                              icon={FaTrash}
                              variant="danger"
                              onClick={() =>
                                handleDelete(
                                  message.id,
                                )
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {messages.map(
              (message) => {
                const read =
                  isReadFlag(
                    message.read_message,
                  );

                const {
                  date,
                  time,
                } = formatDateTime(
                  message.created_at,
                );

                return (
                  <article
                    key={message.id}
                    className={`
                      rounded-2xl border p-4
                      ${
                        read
                          ? "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
                          : "border-blue-200 bg-blue-50/50 dark:border-blue-500/20 dark:bg-blue-500/5"
                      }
                    `}
                  >
                    <div className="flex justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold">
                          {date}
                        </p>

                        <p className="text-[10px] text-slate-400">
                          {time}
                        </p>
                      </div>

                      {!read ? (
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-[9px] font-black uppercase text-[#013E8E]">
                          Nouveau
                        </span>
                      ) : null}
                    </div>

                    <h4 className="mt-3 font-bold text-slate-950 dark:text-white">
                      {message.name}
                    </h4>

                    <p className="mt-1 text-xs text-[#013E8E] dark:text-blue-300">
                      {message.email}
                    </p>

                    <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {message.message}
                    </p>

                    <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                      <DashboardActionButton
                        icon={
                          read
                            ? FaEyeSlash
                            : FaCheck
                        }
                        onClick={() =>
                          toggleReadStatus(
                            message.id,
                          )
                        }
                      >
                        {read
                          ? "Non lu"
                          : "Marquer lu"}
                      </DashboardActionButton>

                      <DashboardActionButton
                        compact
                        icon={FaTrash}
                        variant="danger"
                        onClick={() =>
                          handleDelete(
                            message.id,
                          )
                        }
                      />
                    </div>
                  </article>
                );
              },
            )}
          </div>
        </>
      )}
    </div>
  );
}